import { useMemo } from 'react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WavesOutlinedIcon from '@mui/icons-material/WavesOutlined';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { useGetCommandCenterQuery } from '@/apis/extended/userInterestRateApi';
import type { InterestRateCommandCenter } from '@/apis/extended/userInterestRateApi';
import { useAuth } from '@/hooks/useAuth';

function formatMoney(n: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatPct(n: number, digits = 3) {
  return `${n.toFixed(digits)}%`;
}

const BASE_LABELS: Record<string, string> = {
  conventional: 'Conv',
  fha: 'FHA',
  va: 'VA',
  usda: 'USDA',
  dscr: 'Investment',
  fixAndFlip: 'F&F',
};

const INVESTOR_LOAN_PROGRAMS = new Set([
  'dscr',
  'commercial',
  'bridge',
  'fix_and_flip',
  'fixAndFlip',
]);

export function buildRateInsightChipLabel(
  data: InterestRateCommandCenter
): string {
  const lean = data.lockFloatSuggestion === 'lock' ? 'Lock' : 'Float';
  const rate = `${data.adjustedRatePercent.toFixed(2)}%`;
  if (data.rateChangeFromPrior != null) {
    const sign = data.rateChangeFromPrior >= 0 ? '+' : '';
    return `${lean} · ${rate} · ${sign}${data.rateChangeFromPrior.toFixed(
      2
    )}% vs prior`;
  }
  return `${lean} · ${rate} · ${data.effectiveDate}`;
}

export type RateCommandCenterPresentation = 'elevatedCard' | 'popoverBody';

export type RateCommandCenterPanelProps = {
  presentation?: RateCommandCenterPresentation;
};

export function RateCommandCenterPanel({
  presentation = 'elevatedCard',
}: RateCommandCenterPanelProps) {
  const theme = useTheme();
  const { user, isAuthenticated } = useAuth();

  const { data, isLoading, isError } = useGetCommandCenterQuery(undefined, {
    skip: !isAuthenticated || !user?._id,
    pollingInterval: 120_000,
  });

  const programLabel = useMemo(() => {
    const p = data?.loanProgram ?? 'conventional';
    if (INVESTOR_LOAN_PROGRAMS.has(p)) return 'Investment';
    const words = p.replace(/_/g, ' ').split(' ');
    return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }, [data?.loanProgram]);

  if (!isAuthenticated) {
    return null;
  }

  const border = `1px solid ${alpha(theme.palette.primary.main, 0.22)}`;

  const body = (
    <>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: 1 }}
          >
            Rate command center
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25 }}>
            Admin rate sheet ·{' '}
            {data?.effectiveDate ?? (
              <Skeleton
                component="span"
                width={100}
                sx={{ display: 'inline-block' }}
              />
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Pulled from the same interest-rate configuration your admins
            publish; compared to the prior snapshot when history exists.
          </Typography>
        </Box>
        {isLoading ? (
          <Skeleton variant="rounded" width={120} height={32} />
        ) : data ? (
          <Chip
            icon={
              data.lockFloatSuggestion === 'lock' ? (
                <LockOutlinedIcon sx={{ fontSize: 18 }} />
              ) : (
                <WavesOutlinedIcon sx={{ fontSize: 18 }} />
              )
            }
            label={
              data.lockFloatSuggestion === 'lock' ? 'Lean: lock' : 'Lean: float'
            }
            color={data.lockFloatSuggestion === 'lock' ? 'warning' : 'info'}
            variant="filled"
            sx={{ fontWeight: 700 }}
          />
        ) : null}
      </Stack>

      {isError ? (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          We couldn&apos;t load rate intelligence right now. Try again in a
          moment.
        </Typography>
      ) : isLoading ? (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Skeleton height={28} />
          <Skeleton height={22} />
          <Skeleton height={22} />
        </Stack>
      ) : data ? (
        <>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
            {(Object.keys(BASE_LABELS) as (keyof typeof BASE_LABELS)[]).map(
              (key) => (
                <Chip
                  key={key}
                  size="small"
                  variant="outlined"
                  label={`${BASE_LABELS[key]} ${formatPct(
                    data.baseRates[key],
                    3
                  )}`}
                />
              )
            )}
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            sx={{ mt: 2.5 }}
            alignItems={{ xs: 'stretch', sm: 'flex-end' }}
          >
            <Box flex={1}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                Your scenario ({programLabel}, {data.inputsUsed.ltv}% LTV, FICO{' '}
                {data.inputsUsed.creditScore})
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
                {formatPct(data.adjustedRatePercent)}{' '}
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                >
                  adjusted note rate
                </Typography>
              </Typography>
              {data.rateChangeFromPrior != null &&
              data.priorAdjustedRatePercent != null ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Prior admin snapshot:{' '}
                  {formatPct(data.priorAdjustedRatePercent)} (
                  {data.rateChangeFromPrior >= 0 ? '+' : ''}
                  {formatPct(data.rateChangeFromPrior, 3)} vs today)
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  No earlier admin snapshot to compare yet.
                </Typography>
              )}
            </Box>

            <Box flex={1}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600 }}
              >
                Buying power (income / debt model)
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>
                {data.inputsUsed.monthlyIncome > 0
                  ? formatMoney(data.buyingPower)
                  : '—'}
              </Typography>
              {data.inputsUsed.monthlyIncome <= 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Add monthly income in pre-qualification to see a personalized
                  buying-power number.
                </Typography>
              ) : data.buyingPowerDelta != null ? (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    color:
                      data.buyingPowerDelta >= 0
                        ? theme.palette.success.main
                        : theme.palette.error.main,
                    fontWeight: 600,
                  }}
                >
                  Effect vs prior admin update:{' '}
                  {data.buyingPowerDelta >= 0 ? '+' : ''}
                  {formatMoney(data.buyingPowerDelta)}
                  {data.buyingPowerDeltaPercent != null
                    ? ` (${
                        data.buyingPowerDeltaPercent >= 0 ? '+' : ''
                      }${data.buyingPowerDeltaPercent.toFixed(1)}%)`
                    : ''}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Effect on buying power will appear after a second admin rate
                  snapshot exists.
                </Typography>
              )}
            </Box>
          </Stack>

          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            {data.lockFloatRationale}
          </Typography>
        </>
      ) : null}
    </>
  );

  if (presentation === 'popoverBody') {
    return <Box sx={{ minWidth: { xs: 280, sm: 400 } }}>{body}</Box>;
  }

  return (
    <Card
      elevation={0}
      sx={{
        mb: 3,
        border,
        borderRadius: 2,
        background: alpha(theme.palette.primary.main, 0.04),
      }}
    >
      <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
        {body}
      </CardContent>
    </Card>
  );
}
